/**
 * Ask for a sign-in link.
 *
 * There are no passwords in this product, so this page is the whole front door
 * for hospital staff.
 *
 * The screen deliberately tells the user nothing about whether their address
 * exists. The backend's response is identical either way, and the confirmation
 * below repeats that wording rather than improving on it — "check your inbox"
 * is honest, "we've sent it to you" would not be.
 */

import { useState, type FormEvent } from 'react';
import { homeFor } from './home';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { requestSignInLink } from './signInLink';
import { ResendLink } from './ResendLink';
import { initiateSsoLogin, needsSso, offersSso, resolveEmailOrg, type EmailOrg } from './sso';
import { isCallFailure } from '../lib/callable';
import { useAuth } from './useAuth';
import { clearDestination, isSafeDestination, peekDestination } from './returnTo';
import { Spinner } from '../app/Spinner';
import styles from './SignIn.module.css';
import { EMAIL_MAX } from '../types/fieldLimits';
import { Wordmark } from '../shared/Wordmark';

type Status = 'idle' | 'sending' | 'sso' | 'choose' | 'sent' | 'error';

export function SignInPage() {
  const { loading, session } = useAuth();
  const location = useLocation() as { state?: { from?: string } };
  // An invitation email links here with the invited address filled in; the
  // invitation is accepted by asking for the link.
  const [params] = useSearchParams();
  const invited = (params.get('email') ?? '').trim().slice(0, EMAIL_MAX);

  const [email, setEmail] = useState(invited);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [invalid, setInvalid] = useState(false);
  // In mixed mode the person picks the door; the organization is kept for
  // the Microsoft button. `breakGlass` is the System Admin asking for the
  // emailed link where SSO is required; the backend decides whether they
  // may have it, and answers neutrally either way.
  const [ssoOrg, setSsoOrg] = useState<(EmailOrg & { orgId: string }) | null>(null);
  const [breakGlass, setBreakGlass] = useState(false);

  if (loading) return <Spinner label="Checking your session" />;
  if (session) {
    // Router state for the ordinary case, the stored copy for somebody who
    // arrived at this page in a new tab. Both are validated; neither can name
    // anywhere but a route of this application. See auth/returnTo.ts.
    const back = location.state?.from ?? peekDestination();
    clearDestination();
    return <Navigate to={back && isSafeDestination(back) ? back : homeFor(session)} replace />;
  }

  async function goToProvider(org: EmailOrg & { orgId: string }, value: string) {
    setStatus('sso');
    setMessage(`Taking you to ${org.orgName ?? 'your organization'}\u2019s sign-in\u2026`);
    const { redirectUrl } = await initiateSsoLogin(org.orgId, value);
    window.location.assign(redirectUrl);
  }

  async function sendLink(value: string) {
    setStatus('sending');
    try {
      const result = await requestSignInLink(value);
      setMessage(result.message);
      setStatus('sent');
    } catch (err) {
      setMessage(isCallFailure(err) ? err.message : 'Something went wrong. Please try again.');
      setStatus('error');
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();

    // Trimmed here as well as on the backend. The backend normalizes before
    // validating, so a pasted address with a trailing space works either way —
    // this just keeps the field showing what will actually be sent.
    const value = email.trim();
    if (!value || !value.includes('@')) {
      setInvalid(true);
      return;
    }

    setInvalid(false);
    setStatus('sending');
    try {
      // The address decides the door. A domain that belongs to an
      // organization with single sign-on goes to its identity provider; a
      // lookup that fails for any reason falls through to the emailed link,
      // which is never the wrong answer for an ordinary member.
      const org = await resolveEmailOrg(value).catch(() => null);
      if (needsSso(org) && !breakGlass) {
        await goToProvider(org, value);
        return;
      }
      if (offersSso(org) && !breakGlass) {
        setSsoOrg(org);
        setStatus('choose');
        return;
      }
      if (org?.found && org.authMode === 'sso_only' && !org.ssoEnabled && !breakGlass) {
        throw new Error('Your organization signs in through its own identity provider, which is not available right now. Ask your IT team.');
      }
      const result = await requestSignInLink(value);
      setMessage(result.message);
      setStatus('sent');
    } catch (err) {
      setMessage(
        isCallFailure(err) ? err.message : err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
      setStatus('error');
    }
  }

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Wordmark withIcon className={styles.brandMark} />
        </div>

        {status === 'sso' ? (
          <>
            <h1 className={styles.title}>One moment</h1>
            <div className={styles.sent} role="status" aria-live="polite">
              <p style={{ margin: 0 }}>{message}</p>
            </div>
          </>
        ) : status === 'choose' && ssoOrg ? (
          <>
            <h1 className={styles.title}>How would you like to sign in?</h1>
            <p className={styles.lede}>
              {ssoOrg.orgName ?? 'Your organization'} offers Microsoft sign-in. The emailed link still works too.
            </p>
            <p className={styles.sentTitle} style={{ marginBottom: 'var(--sp-3)' }}>{email.trim()}</p>
            <button
              type="button"
              className={styles.submit}
              onClick={() => { void goToProvider(ssoOrg, email.trim()).catch((err) => { setMessage(isCallFailure(err) ? err.message : 'Microsoft sign-in could not start.'); setStatus('error'); }); }}
            >
              Continue with Microsoft
            </button>
            <button type="button" className={styles.again} onClick={() => void sendLink(email.trim())}>
              Email me a sign-in link instead
            </button>
            <button type="button" className={styles.again} style={{ marginLeft: 'var(--sp-4)' }} onClick={() => { setSsoOrg(null); setStatus('idle'); }}>
              Use a different address
            </button>
          </>
        ) : status === 'sent' ? (
          <>
            <h1 className={styles.title}>Check your email</h1>
            {/* aria-live so a screen-reader user hears the change; the form
                they submitted has just been replaced by this. */}
            <div className={styles.sent} role="status" aria-live="polite">
              <p className={styles.sentTitle}>{email.trim()}</p>
              <p style={{ margin: 0 }}>{message}</p>
            </div>
            <ResendLink
              email={email.trim()}
              send={() => requestSignInLink(email.trim())}
              buttonClassName={styles.again ?? ''}
              className={styles.resend}
            />
            <button
              type="button"
              className={styles.again}
              onClick={() => {
                setStatus('idle');
                setMessage('');
              }}
            >
              Use a different address
            </button>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Sign in</h1>
            <p className={styles.lede}>
              {invited
                ? 'You were invited with this address. Ask for a link and it will sign you in; there is no password to set.'
                : 'Enter your work email and we will send you a link. No password needed — the link signs you in and expires in 15 minutes.'}
            </p>

            {status === 'error' && (
              <div className={styles.error} role="alert">
                {message}
              </div>
            )}

            <form onSubmit={onSubmit} noValidate>
              <label className={styles.label} htmlFor="email">
                Work email
              </label>
              <input
                id="email"
            maxLength={EMAIL_MAX}
                className={styles.input}
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setInvalid(false);
                }}
                aria-invalid={invalid}
                aria-describedby={invalid ? 'email-error' : undefined}
                placeholder="you@hospital.org"
                disabled={status === 'sending'}
              />
              {invalid && (
                <p className={styles.fieldError} id="email-error">
                  Enter an email address so we know where to send the link.
                </p>
              )}

              <button
                className={styles.submit}
                type="submit"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
              </button>
            </form>
          </>
        )}

        <p className={styles.foot}>
          If your organization requires single sign-on, you will be sent to it
          automatically.{' '}
          {status === 'idle' || status === 'error' ? (
            breakGlass ? (
              <>Asking for the emailed link as the System Admin.</>
            ) : (
              <>
                System Admin locked out of it?{' '}
                <button type="button" className={styles.again} style={{ marginTop: 0 }} onClick={() => setBreakGlass(true)}>
                  Ask for an email link instead
                </button>
                .
              </>
            )
          ) : null}{' '}
          Trouble signing in? Ask your administrator to check that your account is active.
        </p>
      </div>
    </main>
  );
}
